import React from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';

import SimpleTable from 'components/table/SimpleTable';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { DashboardHelpers } from 'utils';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MobileTable from 'components/table/MobileTable';

const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    padding: 0,
    paddingBottom: '0px !important',
    overflowY: 'auto',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1),
  },
  actions: {
    padding: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  visibleSmall: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
}));

function UsersTable({
  searchText,
  setModal,
  setSelectedUser,
  className,
  users,
  isFetchingUsers,
  organizations,
  selectedUser,
  ...rest
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const mobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const tableHeader = [
    {
      alias: 'name',
      sortable: true,
      mobilePreview: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'login',
      dataRender: (s) => (
        <div className={classes.nameCell}>
          <div>
            {!!s.login && s.login}
            <div className={classes.visibleSmall}>{s.email}</div>
          </div>
        </div>
      ),
    },
    {
      alias: 'full_name',
      sortable: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'display_name',
      dataRender: (s) => (
        <div className={classes.nameCell}>
          <div>
            {!!s.display_name && s.display_name}
            <div className={classes.visibleSmall}>{s.email}</div>
          </div>
        </div>
      ),
    },
    {
      alias: 'org',
      sortable: true,
      mobileInfo: true,
      format: 'string',
      dataAlias: 'organization_id',
      className: 'hiddenMedium',
      dataRender: (s) => (
        <div className={classes.nameCell}>
          <div>
            {!!s.organization_id && _.find(organizations, { id: s.organization_id })?.name}
            <div className={classes.visibleSmall}>{s.email}</div>
          </div>
        </div>
      ),
    },
  ];

  const handleSelect = (event, user) => {
    setSelectedUser(user);
    setModal(true);
  };

  const searchUsers = () => {
    const searchedUsers = _.filter(users, (user) => {
      return (
        (user.login && DashboardHelpers.includesCaseInsensitive(user.login, searchText)) ||
        (user.display_name && DashboardHelpers.includesCaseInsensitive(user.display_name, searchText)) ||
        (user.organization_id &&
          organizations &&
          DashboardHelpers.includesCaseInsensitive(
            _.find(organizations, { id: user.organization_id })?.name,
            searchText
          )) ||
        (user.created_by && DashboardHelpers.includesCaseInsensitive(user.created_by, searchText))
      );
    });

    return searchedUsers;
  };

  const dataToRender = (tableHeader, order, orderBy) => {
    const header = _.find(tableHeader, { dataAlias: orderBy });
    let sortedUsers = searchUsers();
    if (header?.dataAlias === 'org') {
      sortedUsers = _.sortBy(sortedUsers, [(o) => _.find(organizations, { id: o.organization_id })?.name]);
    } else if (header?.format === 'string') {
      sortedUsers = _.sortBy(sortedUsers, [(o) => (o[orderBy] ? o[orderBy].toUpperCase().trim() : '')]);
    }
    if (order === 'asc') {
      sortedUsers.reverse();
    }

    return sortedUsers;
  };

  const searchedUsers = searchUsers();

  return mobileView ? (
    <MobileTable title={t('users')} tableHeader={tableHeader} data={searchedUsers} selectRow={handleSelect} />
  ) : (
    <div {...rest} className={clsx(classes.root, className)}>
      <SimpleTable
        title={t('users')}
        tableHeader={tableHeader}
        data={searchedUsers}
        isLoading={isFetchingUsers}
        selected={selectedUser}
        selectRow={handleSelect}
        sortFunc={dataToRender}
        filter={searchText}
      />
    </div>
  );
}

UsersTable.propTypes = {
  setModal: PropTypes.func.isRequired,
  setSelectedUser: PropTypes.func.isRequired,
  users: PropTypes.array,
  className: PropTypes.string,
  isFetchingUsers: PropTypes.bool,
  organizations: PropTypes.array,
  searchText: PropTypes.string,
  selectedUser: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

UsersTable.defaultProps = {
  users: [],
  className: undefined,
  isFetchingUsers: true,
  organizations: [],
  searchText: '',
  selectedUser: undefined,
};

export default UsersTable;
